import React, { useEffect, useMemo, useRef, useState } from 'react';
import './custom.css';
import castleArt from './assets/login/castle.png';
import oliveBranchArt from './assets/login/olive-branch.png';
import mixedArt from './assets/login/mixed.png';

const emptyCategory = { name: '', printName: '', nameSp: '', nameEn: '', extern: false };
const emptyProduct = { nameEn: '', nameSp: '', price: '0', isActive: true };
const defaultFooterWarningSp = 'Advertencia: El consumo de productos crudos o poco cocinados, tales como carnes, aves, moluscos o huevos, puede aumentar el riesgo de enfermedades transmitidas por estos, especialmente si usted tiene ciertas condiciones medicas.';
const defaultFooterWarningEn = 'Warning: Consuming raw or undercooked meats, poultry, shellfish or eggs may increase your risk of foodborne illness, especially if you have certain medical conditions.';
const emptyConfig = { companyName: '', pathFile: '', dateShowed: '', footerWarningSp: defaultFooterWarningSp, footerWarningEn: defaultFooterWarningEn };
const emptyRestaurant = {
  name: '',
  address: '',
  publicSlug: '',
  isActive: true,
  ftpEnabled: false,
  ftpHost: '',
  ftpPort: 21,
  ftpUserName: '',
  ftpPassword: '',
  ftpRemotePath: '',
  ftpUseSsl: false,
  footerWarningSp: '',
  footerWarningEn: '',
};
const emptyUser = { userName: '', displayName: '', password: '', role: 'Manager', isActive: true, restaurantIds: [] };
const emptyBusiness = { name: '', publicSlug: '', isActive: true };
const emptyPasswordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
const defaultLoginForm = { userName: 'admin', password: '', rememberMe: true };
const emptyCopyForm = { sourceRestaurantId: '', sourceDate: '', replaceExisting: true };
const emptyImportForm = { file: null, replaceExisting: true };
const emptyCatalogImportForm = { file: null, updateExisting: true };
const emptyLogFilter = { date: '', type: '', search: '' };
const emptyReports = { menuCount: 0, publishedCount: 0, draftCount: 0, closedCount: 0, activeProducts: 0, inactiveProducts: 0, topProducts: [], recentMenus: [] };

function isPhoneScreen() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches;
}

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function parsePrice(value) {
  const normalized = (value || '').toString().replace(/[^0-9.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCurrency(value) {
  return currency.format(parsePrice(value));
}

function formatPriceInput(value) {
  return formatCurrency(value);
}

function cleanPriceInput(value) {
  const parsed = parsePrice(value);
  return parsed ? parsed.toFixed(2) : '';
}

function normalizeText(value) {
  return (value || '').toString().toLowerCase();
}

function primaryProductName(item) {
  return item?.nameSp || item?.nameEn || 'Sin nombre';
}

function secondaryProductName(item) {
  if (!item?.nameEn || item.nameEn === item.nameSp) {
    return 'Sin traduccion';
  }

  return item.nameEn;
}

function categoryDisplayName(category) {
  if (!category) {
    return 'Categorias';
  }

  const primary = category.nameSp || category.printName || category.name || category.nameEn || 'Categoria';
  const secondary = category.nameEn || category.printName || category.name;
  if (secondary) {
    return `${primary} - (${secondary})`;
  }

  return primary;
}

function productPayload(product, categoryId, businessId = null) {
  return {
    categoryID: categoryId,
    nameEn: product.nameEn.trim(),
    nameSp: product.nameSp.trim() || null,
    price: parsePrice(product.price),
    isActive: product.isActive,
    businessId,
  };
}

async function readError(response, fallback) {
  const text = await response.text();
  if (!text) {
    return fallback;
  }

  try {
    const payload = JSON.parse(text);
    return payload.message || payload.title || payload.error || fallback;
  } catch {
    return text.replaceAll('"', '') || fallback;
  }
}

function PlusIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M12 5v14M5 12l7 7 7-7" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14M9 7V4h6v3" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 20h4l11-11a2.8 2.8 0 0 0-4-4L4 16v4ZM13.5 6.5l4 4" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M14 10a4 4 0 1 0-2.4 3.7L14 16h3v3h3v-3.2L15.2 11" />
      <path d="M7 10h.01" />
    </svg>
  );
}

function BrandMark({ compact = false, showCuisine = true }) {
  return (
    <div className={compact ? 'brand-mark compact' : 'brand-mark'}>
      <span className="brand-icon" aria-hidden="true">
        <svg viewBox="0 0 32 32">
          <path d="M7 16h18c0 6-4 10-9 10S7 22 7 16Z" />
          <path d="M10 16c0-3 2.4-5 6-5s6 2 6 5" />
          <path d="M13 10c-1.2-1.6-.2-3.2 1.6-4.3M18 10c1.4-1 .9-2.9-.4-4.2" />
          <path d="M9 20h14" />
        </svg>
      </span>
      <span className="brand-title">MENU</span>
      <span className="brand-subtitle">Especial del dia</span>
      {!compact && showCuisine && <span className="brand-cuisine">Cocina espanola</span>}
    </div>
  );
}

export default function App() {
  const userMenuRef = useRef(null);
  const [mode, setMode] = useState('menu');
  const [phoneMode, setPhoneMode] = useState(isPhoneScreen);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [menu, setMenu] = useState([]);
  const [menuStatus, setMenuStatus] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [menuHistory, setMenuHistory] = useState([]);
  const [logs, setLogs] = useState([]);
  const [logDates, setLogDates] = useState([]);
  const [logFilter, setLogFilter] = useState(emptyLogFilter);
  const [reports, setReports] = useState(emptyReports);
  const [activeRestaurantId, setActiveRestaurantId] = useState(null);
  const [activeBusinessId, setActiveBusinessId] = useState(null);
  const [config, setConfig] = useState(emptyConfig);
  const [configForm, setConfigForm] = useState(emptyConfig);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [restaurantForm, setRestaurantForm] = useState(emptyRestaurant);
  const [userForm, setUserForm] = useState(emptyUser);
  const [businessForm, setBusinessForm] = useState(emptyBusiness);
  const [loginForm, setLoginForm] = useState(defaultLoginForm);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [copyForm, setCopyForm] = useState(emptyCopyForm);
  const [importForm, setImportForm] = useState(emptyImportForm);
  const [catalogImportForm, setCatalogImportForm] = useState(emptyCatalogImportForm);
  const [showPasswordPanel, setShowPasswordPanel] = useState(false);
  const [showCopyPanel, setShowCopyPanel] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingRestaurantId, setEditingRestaurantId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingBusinessId, setEditingBusinessId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [busyProductId, setBusyProductId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    checkSession();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 720px)');
    const syncPhoneMode = () => setPhoneMode(mediaQuery.matches);

    syncPhoneMode();
    mediaQuery.addEventListener('change', syncPhoneMode);
    return () => mediaQuery.removeEventListener('change', syncPhoneMode);
  }, []);

  useEffect(() => {
    if (activeCategoryId) {
      loadProducts(activeCategoryId);
    }
  }, [activeCategoryId]);

  useEffect(() => {
    if (activeRestaurantId) {
      loadMenu(activeRestaurantId, configForm.dateShowed || config.dateShowed);
      loadMenuHistory(activeRestaurantId);
    }
  }, [activeRestaurantId, configForm.dateShowed]);

  useEffect(() => {
    if (phoneMode) {
      return;
    }

    if (mode === 'reports') {
      loadReports();
    }
    if (mode === 'logs' && currentUser?.role === 'System') {
      loadLogs();
    }
  }, [mode, activeRestaurantId, phoneMode]);

  useEffect(() => {
    if (phoneMode && mode !== 'menu') {
      setMode('menu');
      return;
    }

    if (mode === 'logs' && currentUser?.role !== 'System') {
      setMode('menu');
    }
  }, [mode, currentUser, phoneMode]);

  useEffect(() => {
    if (!showUserMenu) {
      return undefined;
    }

    function closeUserMenu(event) {
      if (!userMenuRef.current?.contains(event.target)) {
        setShowUserMenu(false);
      }
    }

    document.addEventListener('mousedown', closeUserMenu);
    return () => document.removeEventListener('mousedown', closeUserMenu);
  }, [showUserMenu]);

  function menuQuery() {
    const query = new URLSearchParams();
    const date = configForm.dateShowed || config.dateShowed;
    if (activeBusinessId) {
      query.set('businessId', activeBusinessId);
    }
    if (activeRestaurantId) {
      query.set('restaurantId', activeRestaurantId);
    }
    if (date) {
      query.set('date', date);
    }
    const text = query.toString();
    return text ? `?${text}` : '';
  }

  function businessQuery(extra = {}) {
    const query = new URLSearchParams();
    if (activeBusinessId) {
      query.set('businessId', activeBusinessId);
    }
    Object.entries(extra).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query.set(key, value);
      }
    });
    const text = query.toString();
    return text ? `?${text}` : '';
  }

  function withMenuCounts(categoriesData, menuData) {
    const counts = new Map(menuData.map((category) => [category.id, category.items.length]));
    return categoriesData.map((category) => ({
      ...category,
      menuProductCount: counts.get(category.id) ?? 0,
    }));
  }

  async function getLatestMenuDate(restaurantId, fallbackDate = '', businessId = activeBusinessId) {
    if (!restaurantId) {
      return fallbackDate;
    }

    const response = await fetch(`/api/menu/latest-date?${new URLSearchParams({
      restaurantId,
      ...(businessId ? { businessId } : {}),
    }).toString()}`);
    if (!response.ok) {
      return fallbackDate;
    }

    const data = await response.json();
    return (data.menuDate || fallbackDate || '').slice(0, 10);
  }

  async function checkSession() {
    try {
      const response = await fetch('/api/auth/me');
      if (!response.ok) {
        setCurrentUser(null);
        return;
      }

      const user = await response.json();
      setCurrentUser(user);
      setLoginForm((current) => ({ ...current, password: '' }));
      const businessId = await loadBusinessesForUser(user);
      await refreshShell(user, null, businessId);
      if (user.role === 'Admin' || user.role === 'System') {
        await loadUsers();
      }
    } catch {
      setCurrentUser(null);
    } finally {
      setAuthChecked(true);
    }
  }

  async function login(event) {
    event.preventDefault();
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });

      if (!response.ok) {
        throw new Error(await readError(response, 'No se pudo iniciar sesion.'));
      }

      const user = await response.json();
      setCurrentUser(user);
      setLoginForm((current) => ({ ...current, password: '' }));
      const businessId = await loadBusinessesForUser(user);
      await refreshShell(user, null, businessId);
      if (user.role === 'Admin' || user.role === 'System') {
        await loadUsers();
      }
    } catch (err) {
      setError(err.message);
    }
  }

  async function logout() {
    await fetch('/api/auth/logout', { method: 'POST' });
    setCurrentUser(null);
    setMode('menu');
    setMenu([]);
    setRestaurants([]);
    setBusinesses([]);
    setActiveBusinessId(null);
    setCategories([]);
    setProducts([]);
    setMenuStatus(null);
    setPasswordForm(emptyPasswordForm);
    setLoginForm(defaultLoginForm);
    setShowPasswordPanel(false);
  }

  async function changePassword(event) {
    event.preventDefault();
    setError('');
    setNotice('');

    try {
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordForm),
      });

      if (!response.ok) {
        throw new Error(await readError(response, 'No se pudo cambiar la contrasena.'));
      }

      const user = await response.json();
      setCurrentUser(user);
      setPasswordForm(emptyPasswordForm);
      setShowPasswordPanel(false);
      setNotice('Contrasena actualizada.');
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadBusinessesForUser(user = currentUser) {
    if (user?.role !== 'System') {
      const userBusinessId = user?.businessId ?? null;
      setBusinesses([]);
      setActiveBusinessId(userBusinessId);
      return userBusinessId;
    }

    try {
      const response = await fetch('/api/businesses');
      if (!response.ok) {
        return activeBusinessId;
      }

      const data = await response.json();
      const selected = activeBusinessId ?? data.find((business) => business.isActive)?.id ?? data[0]?.id ?? null;
      setBusinesses(data);
      setActiveBusinessId(selected);
      return selected;
    } catch {
      setBusinesses([]);
      return activeBusinessId;
    }
  }

  async function refreshShell(user = currentUser, selectedDateOverride = null, selectedBusinessId = activeBusinessId) {
    setLoading(true);
    setError('');

    try {
      const query = selectedBusinessId ? `?${new URLSearchParams({ businessId: selectedBusinessId }).toString()}` : '';
      const [categoriesResponse, configResponse, restaurantsResponse] = await Promise.all([
        fetch(`/api/categories${query}`),
        fetch(`/api/config${query}`),
        fetch(`/api/restaurants${query}`),
      ]);

      if (!categoriesResponse.ok || !configResponse.ok || !restaurantsResponse.ok) {
        throw new Error('No se pudo cargar la informacion del menu.');
      }

      const categoriesData = await categoriesResponse.json();
      const configData = await configResponse.json();
      const restaurantsData = await restaurantsResponse.json();
      const normalizedConfig = {
        companyName: configData.companyName || '',
        pathFile: configData.pathFile || '',
        dateShowed: (configData.dateShowed || '').slice(0, 10),
        footerWarningSp: configData.footerWarningSp || defaultFooterWarningSp,
        footerWarningEn: configData.footerWarningEn || defaultFooterWarningEn,
      };
      const effectiveDate = selectedDateOverride || normalizedConfig.dateShowed;

      const restaurantId = restaurantsData.some((restaurant) => restaurant.id === activeRestaurantId) ? activeRestaurantId : restaurantsData[0]?.id ?? null;
      const latestDate = await getLatestMenuDate(restaurantId, normalizedConfig.dateShowed, selectedBusinessId);
      const menuQueryText = restaurantId || effectiveDate ? `?${new URLSearchParams({
        ...(selectedBusinessId ? { businessId: selectedBusinessId } : {}),
        ...(restaurantId ? { restaurantId } : {}),
        ...((selectedDateOverride || latestDate || effectiveDate) ? { date: selectedDateOverride || latestDate || effectiveDate } : {}),
      }).toString()}` : '';
      const [menuResponse, statusResponse] = await Promise.all([
        fetch(`/api/menu${menuQueryText}`),
        fetch(`/api/menu/status${menuQueryText}`),
      ]);
      if (!menuResponse.ok || !statusResponse.ok) {
        throw new Error('No se pudo cargar el menu del restaurante.');
      }

      const menuData = await menuResponse.json();
      const statusData = await statusResponse.json();
      setRestaurants(restaurantsData);
      setActiveRestaurantId(restaurantId);
      setCopyForm((current) => ({ ...current, sourceRestaurantId: current.sourceRestaurantId || restaurantId || '' }));
      setCategories(withMenuCounts(categoriesData, menuData));
      setMenu(menuData);
      setMenuStatus(statusData);
      setConfig(normalizedConfig);
      setConfigForm({ ...normalizedConfig, dateShowed: selectedDateOverride || latestDate || effectiveDate });
      const nextCategory = await firstCategoryWithProducts(categoriesData, activeCategoryId ?? categoriesData[0]?.id ?? null, selectedBusinessId);
      setActiveCategoryId(nextCategory.categoryId);
      setProducts(nextCategory.products);
      setSearch('');
      if (user?.role === 'Admin' || user?.role === 'System') {
        await loadUsers(selectedBusinessId);
      }
      if (restaurantId) {
        await loadMenuHistory(restaurantId);
      }
    } catch (err) {
      if (err.message.includes('401')) {
        setCurrentUser(null);
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMenu(restaurantId = activeRestaurantId, date = configForm.dateShowed || config.dateShowed, businessId = activeBusinessId) {
    if (!restaurantId) {
      return;
    }

    try {
      const query = new URLSearchParams({ restaurantId });
      if (businessId) {
        query.set('businessId', businessId);
      }
      if (date) {
        query.set('date', date);
      }

      const [categoriesResponse, menuResponse] = await Promise.all([
        fetch(`/api/categories${businessId ? `?${new URLSearchParams({ businessId }).toString()}` : ''}`),
        fetch(`/api/menu?${query.toString()}`),
      ]);

      const statusResponse = await fetch(`/api/menu/status?${query.toString()}`);
      if (!categoriesResponse.ok || !menuResponse.ok || !statusResponse.ok) {
        throw new Error('No se pudo cargar el menu del restaurante.');
      }

      const categoriesData = await categoriesResponse.json();
      const menuData = await menuResponse.json();
      const statusData = await statusResponse.json();
      setCategories(withMenuCounts(categoriesData, menuData));
      setMenu(menuData);
      setMenuStatus(statusData);
      const nextCategory = await firstCategoryWithProducts(categoriesData, activeCategoryId ?? categoriesData[0]?.id ?? null, businessId);
      setActiveCategoryId(nextCategory.categoryId);
      setProducts(nextCategory.products);
      setSearch('');
    } catch (err) {
      setError(err.message);
    }
  }

  async function firstCategoryWithProducts(categoriesData, preferredCategoryId = null, businessId = activeBusinessId) {
    if (!categoriesData.length) {
      return { categoryId: null, products: [] };
    }

    const orderedCategoryIds = [
      preferredCategoryId,
      ...categoriesData.map((category) => category.id),
    ].filter((categoryId, index, values) => categoryId && values.indexOf(categoryId) === index);

    for (const categoryId of orderedCategoryIds) {
      try {
        const response = await fetch(`/api/products?${new URLSearchParams({
          categoryId,
          ...(businessId ? { businessId } : {}),
        }).toString()}`);
        if (response.ok) {
          const productsData = await response.json();
          if (productsData.length) {
            return { categoryId, products: productsData };
          }
        }
      } catch {
        // Keep looking for a usable category.
      }
    }

    return { categoryId: categoriesData[0]?.id ?? null, products: [] };
  }

  async function changeRestaurant(restaurantId) {
    const latestDate = await getLatestMenuDate(restaurantId, configForm.dateShowed || config.dateShowed);
    setConfigForm((current) => ({ ...current, dateShowed: latestDate }));
    setActiveRestaurantId(restaurantId);
  }

  async function changeBusiness(businessId) {
    setActiveBusinessId(businessId);
    setActiveRestaurantId(null);
    setActiveCategoryId(null);
    setProducts([]);
    setMenu([]);
    setUsers([]);
    setMenuHistory([]);
    await refreshShell(currentUser, null, businessId);
  }

  async function loadMenuHistory(restaurantId = activeRestaurantId) {
    if (!restaurantId) {
      setMenuHistory([]);
      return;
    }

    try {
      const response = await fetch(`/api/menu/history?restaurantId=${restaurantId}`);
      if (!response.ok) {
        return;
      }

      setMenuHistory(await response.json());
    } catch {
      setMenuHistory([]);
    }
  }

  async function loadConfig(selectedDateOverride = null) {
    try {
      const response = await fetch(`/api/config${businessQuery()}`);
      if (!response.ok) {
        throw new Error('No se pudo cargar la configuracion.');
      }

      const data = await response.json();
      const normalized = {
        companyName: data.companyName || '',
        pathFile: data.pathFile || '',
        dateShowed: (data.dateShowed || '').slice(0, 10),
        footerWarningSp: data.footerWarningSp || defaultFooterWarningSp,
        footerWarningEn: data.footerWarningEn || defaultFooterWarningEn,
      };
      setConfig(normalized);
      setConfigForm({ ...normalized, dateShowed: selectedDateOverride || normalized.dateShowed });
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadUsers(businessId = activeBusinessId) {
    try {
      const response = await fetch(`/api/users${businessId ? `?${new URLSearchParams({ businessId }).toString()}` : ''}`);
      if (!response.ok) {
        return;
      }

      setUsers(await response.json());
    } catch {
      setUsers([]);
    }
  }

  async function loadReports() {
    try {
      const query = new URLSearchParams();
      if (activeBusinessId) {
        query.set('businessId', activeBusinessId);
      }
      if (activeRestaurantId) {
        query.set('restaurantId', activeRestaurantId);
      }

      const response = await fetch(`/api/reports/summary?${query.toString()}`);
      if (!response.ok) {
        return;
      }

      setReports(await response.json());
    } catch {
      setReports(emptyReports);
    }
  }

  async function loadLogs(filter = logFilter) {
    try {
      const datesResponse = await fetch('/api/logs/dates');
      const dates = datesResponse.ok ? await datesResponse.json() : [];
      const selectedDate = filter.date || dates[0] || '';
      setLogDates(dates);
      setLogFilter((current) => ({ ...current, ...filter, date: selectedDate }));

      const query = new URLSearchParams();
      if (selectedDate) {
        query.set('date', selectedDate);
      }
      if (filter.type) {
        query.set('type', filter.type);
      }
      if (filter.search) {
        query.set('search', filter.search);
      }

      const response = await fetch(`/api/logs?${query.toString()}`);
      if (!response.ok) {
        return;
      }

      setLogs(await response.json());
    } catch {
      setLogs([]);
    }
  }

  async function loadProducts(categoryId) {
    setProductsLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/products?${new URLSearchParams({
        categoryId,
        ...(activeBusinessId ? { businessId: activeBusinessId } : {}),
      }).toString()}`);
      if (!response.ok) {
        throw new Error('No se pudieron cargar los productos.');
      }

      setProducts(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setProductsLoading(false);
    }
  }

  async function refreshMenuState(date = configForm.dateShowed || config.dateShowed) {
    if (!activeRestaurantId) {
      return;
    }

    const query = new URLSearchParams({ restaurantId: activeRestaurantId });
    if (activeBusinessId) {
      query.set('businessId', activeBusinessId);
    }
    if (date) {
      query.set('date', date);
    }

    const [menuResponse, statusResponse] = await Promise.all([
      fetch(`/api/menu?${query.toString()}`),
      fetch(`/api/menu/status?${query.toString()}`),
    ]);

    if (!menuResponse.ok || !statusResponse.ok) {
      throw new Error('No se pudo refrescar el menu.');
    }

    const menuData = await menuResponse.json();
    const statusData = await statusResponse.json();
    setMenu(menuData);
    setMenuStatus(statusData);
    setCategories((current) => withMenuCounts(current, menuData));
  }

  async function reloadActiveData(message, selectedDateOverride = configForm.dateShowed || config.dateShowed) {
    await refreshShell(currentUser, selectedDateOverride);
    await loadConfig(selectedDateOverride);
    setNotice(message);
  }

  async function keepWorkspacePosition(beforeTop) {
    if (beforeTop === null) {
      return;
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const workspace = document.querySelector('.work-grid');
    if (!workspace) {
      return;
    }

    const afterTop = workspace.getBoundingClientRect().top;
    window.scrollBy({ top: afterTop - beforeTop, behavior: 'auto' });
  }

  async function runMutation(action, successMessage, options = {}) {
    setNotice('');
    setError('');
    const selectedDate = configForm.dateShowed || config.dateShowed;
    const workspaceTop = options.keepPosition
      ? document.querySelector('.work-grid')?.getBoundingClientRect().top ?? null
      : null;

    try {
      const response = await action();
      if (response.status === 401) {
        setCurrentUser(null);
        throw new Error('Tu sesion expiro. Inicia sesion otra vez.');
      }
      if (response.status === 403) {
        throw new Error('No tienes permiso para hacer este cambio.');
      }
      if (!response.ok) {
        throw new Error(await readError(response, 'No se pudo guardar el cambio.'));
      }

      if (options.refreshMenuOnly) {
        await refreshMenuState(selectedDate);
        setNotice(successMessage);
      } else {
        await reloadActiveData(successMessage, selectedDate);
      }
      await keepWorkspacePosition(workspaceTop);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyProductId(null);
    }
  }

  async function addProductToMenu(product) {
    setBusyProductId(product.id);
    await runMutation(
      () => fetch('/api/menu/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productID: product.id,
          restaurantId: activeRestaurantId,
          menuDate: configForm.dateShowed || config.dateShowed,
          businessId: activeBusinessId,
        }),
      }),
      'Producto agregado al menu.',
      { keepPosition: true, refreshMenuOnly: true },
    );
  }

  async function removeProductFromMenu(productId) {
    setBusyProductId(productId);
    await runMutation(
      () => fetch(`/api/menu/items/${productId}${menuQuery()}`, { method: 'DELETE' }),
      'Producto removido del menu.',
      { keepPosition: true, refreshMenuOnly: true },
    );
  }

  async function moveMenuProduct(productId, direction) {
    setBusyProductId(productId);
    await runMutation(
      () => fetch(`/api/menu/items/${productId}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          direction,
          restaurantId: activeRestaurantId,
          menuDate: configForm.dateShowed || config.dateShowed,
          businessId: activeBusinessId,
        }),
      }),
      'Orden actualizado.',
      { keepPosition: true, refreshMenuOnly: true },
    );
  }

  async function clearMenu() {
    if (!window.confirm('Quieres limpiar todos los items del menu?')) {
      return;
    }

    await runMutation(
      () => fetch(`/api/menu/items${menuQuery()}`, { method: 'DELETE' }),
      'Menu limpiado.',
      { keepPosition: true, refreshMenuOnly: true },
    );
  }

  async function copyMenu() {
    const targetDate = configForm.dateShowed || config.dateShowed;
    if (!copyForm.sourceRestaurantId || !copyForm.sourceDate || !activeRestaurantId || !targetDate) {
      setError('Selecciona restaurante origen, fecha origen y fecha destino.');
      return;
    }

    if (!window.confirm('Quieres reemplazar el menu actual con el menu seleccionado?')) {
      return;
    }

    await runMutation(
      () => fetch('/api/menu/copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceRestaurantId: Number(copyForm.sourceRestaurantId),
          sourceDate: copyForm.sourceDate,
          targetRestaurantId: activeRestaurantId,
          targetDate,
          replaceExisting: copyForm.replaceExisting,
          businessId: activeBusinessId,
        }),
      }),
      'Menu copiado.',
    );
    await loadMenuHistory(activeRestaurantId);
  }

  async function importMenu(event) {
    event.preventDefault();
    const date = configForm.dateShowed || config.dateShowed;
    if (!activeRestaurantId || !date) {
      setError('Selecciona restaurante y fecha del menu.');
      return;
    }

    if (!importForm.file) {
      setError('Selecciona un archivo Excel o CSV.');
      return;
    }

    const formData = new FormData();
    formData.append('file', importForm.file);
    formData.append('restaurantId', String(activeRestaurantId));
    formData.append('menuDate', date);
    formData.append('replaceExisting', String(importForm.replaceExisting));
    if (activeCategoryId) {
      formData.append('categoryId', String(activeCategoryId));
    }
    if (activeBusinessId) {
      formData.append('businessId', String(activeBusinessId));
    }

    setNotice('');
    setError('');
    try {
      const response = await fetch('/api/menu/import', {
        method: 'POST',
        body: formData,
      });

      if (response.status === 401) {
        setCurrentUser(null);
        throw new Error('Tu sesion expiro. Inicia sesion otra vez.');
      }
      if (response.status === 403) {
        throw new Error('No tienes permiso para importar este menu.');
      }
      if (!response.ok) {
        throw new Error(await readError(response, 'No se pudo importar el menu.'));
      }

      const result = await response.json();
      await reloadActiveData(
        `Menu importado: ${result.importedItems} items, ${result.createdProducts} productos nuevos, ${result.createdCategories} categorias nuevas, ${result.skippedRows} filas omitidas.`,
        date,
      );
      setImportForm(emptyImportForm);
      event.currentTarget.reset();
    } catch (err) {
      setError(err.message);
    }
  }

  async function importCatalog(event) {
    event.preventDefault();
    if (!catalogImportForm.file) {
      setError('Selecciona un archivo Excel o CSV.');
      return;
    }

    const formData = new FormData();
    formData.append('file', catalogImportForm.file);
    formData.append('updateExisting', String(catalogImportForm.updateExisting));
    if (activeCategoryId) {
      formData.append('categoryId', String(activeCategoryId));
    }
    if (activeBusinessId) {
      formData.append('businessId', String(activeBusinessId));
    }

    setNotice('');
    setError('');
    try {
      const response = await fetch('/api/catalog/import', {
        method: 'POST',
        body: formData,
      });

      if (response.status === 401) {
        setCurrentUser(null);
        throw new Error('Tu sesion expiro. Inicia sesion otra vez.');
      }
      if (response.status === 403) {
        throw new Error('No tienes permiso para importar catalogo.');
      }
      if (!response.ok) {
        throw new Error(await readError(response, 'No se pudo importar el catalogo.'));
      }

      const result = await response.json();
      await reloadActiveData(`Catalogo importado: ${result.createdCategories} categorias nuevas, ${result.createdProducts} productos nuevos, ${result.updatedProducts} productos actualizados, ${result.skippedRows} filas omitidas.`);
      setCatalogImportForm(emptyCatalogImportForm);
      event.currentTarget.reset();
    } catch (err) {
      setError(err.message);
    }
  }

  async function updateMenuStatus(status) {
    await runMutation(
      () => fetch('/api/menu/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurantId: activeRestaurantId,
          menuDate: configForm.dateShowed || config.dateShowed,
          status,
          businessId: activeBusinessId,
        }),
      }),
      status === 'Published' ? 'Menu publicado.' : 'Menu vuelto a Draft.',
    );
    await loadMenu(activeRestaurantId, configForm.dateShowed || config.dateShowed);
  }

  async function sendMenuToFtp() {
    const date = configForm.dateShowed || config.dateShowed;
    if (!activeRestaurantId || !date) {
      setError('Selecciona restaurante y fecha del menu.');
      return;
    }

    await runMutation(
      () => fetch(`/api/export/menu/ftp?${new URLSearchParams({ restaurantId: activeRestaurantId, date, ...(activeBusinessId ? { businessId: activeBusinessId } : {}) }).toString()}`, { method: 'POST' }),
      'PDF enviado por FTP.',
    );
  }

  function openHistoryMenu(entry) {
    setMode('menu');
    setActiveRestaurantId(entry.restaurantId);
    setConfigForm((current) => ({ ...current, dateShowed: (entry.menuDate || '').slice(0, 10) }));
    setSearch('');
  }

  function prepareHistoryCopy(entry) {
    setMode('menu');
    setShowCopyPanel(true);
    setCopyForm({
      sourceRestaurantId: entry.restaurantId,
      sourceDate: (entry.menuDate || '').slice(0, 10),
      replaceExisting: true,
    });
  }

  function openExport(language, print = false) {
    const date = configForm.dateShowed || config.dateShowed;
    const query = new URLSearchParams({ language });
    if (date) {
      query.set('date', date);
    }
    if (activeRestaurantId) {
      query.set('restaurantId', activeRestaurantId);
    }
    if (activeBusinessId) {
      query.set('businessId', activeBusinessId);
    }
    if (print) {
      query.set('print', 'true');
    }
    window.open(`/api/export/menu?${query.toString()}`, '_blank', 'noopener,noreferrer');
  }

  async function saveConfig(event) {
    event.preventDefault();
    await runMutation(
      () => fetch('/api/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: configForm.companyName.trim(),
          pathFile: configForm.pathFile.trim(),
          dateShowed: configForm.dateShowed,
          footerWarningSp: configForm.footerWarningSp.trim(),
          footerWarningEn: configForm.footerWarningEn.trim(),
          businessId: activeBusinessId,
        }),
      }),
      'Configuracion actualizada.',
    );
  }

  function editCategory(category) {
    setEditingCategoryId(category.id);
    setCategoryForm({
      name: category.name,
      printName: category.printName,
      nameSp: category.nameSp,
      nameEn: category.nameEn,
      extern: category.extern,
    });
  }

  function resetCategoryForm() {
    setEditingCategoryId(null);
    setCategoryForm(emptyCategory);
  }

  async function saveCategory(event) {
    event.preventDefault();
    const payload = {
      ...categoryForm,
      name: categoryForm.name.trim(),
      printName: categoryForm.printName.trim(),
      nameSp: categoryForm.nameSp.trim(),
      nameEn: categoryForm.nameEn.trim(),
      order: null,
      businessId: activeBusinessId,
    };

    await runMutation(
      () => fetch(editingCategoryId ? `/api/categories/${editingCategoryId}` : '/api/categories', {
        method: editingCategoryId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
      editingCategoryId ? 'Categoria actualizada.' : 'Categoria creada.',
    );
    resetCategoryForm();
  }

  async function deleteCategory(category) {
    if (!window.confirm(`Quieres eliminar la categoria "${category.name}"?`)) {
      return;
    }

    await runMutation(
      () => fetch(`/api/categories/${category.id}`, { method: 'DELETE' }),
      'Categoria eliminada.',
    );
  }

  async function moveCategory(categoryId, direction) {
    await runMutation(
      () => fetch(`/api/categories/${categoryId}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction }),
      }),
      'Orden de categorias actualizado.',
    );
  }

  function editProduct(product) {
    setEditingProductId(product.id);
    setProductForm({
      nameEn: product.nameEn,
      nameSp: product.nameSp || '',
      price: formatPriceInput(product.price),
      isActive: product.isActive,
    });
  }

  function resetProductForm() {
    setEditingProductId(null);
    setProductForm(emptyProduct);
  }

  function editRestaurant(restaurant) {
    setEditingRestaurantId(restaurant.id);
    setRestaurantForm({
      name: restaurant.name,
      address: restaurant.address || '',
      publicSlug: restaurant.publicSlug || '',
      isActive: restaurant.isActive,
      ftpEnabled: restaurant.ftpEnabled || false,
      ftpHost: restaurant.ftpHost || '',
      ftpPort: restaurant.ftpPort || 21,
      ftpUserName: restaurant.ftpUserName || '',
      ftpPassword: restaurant.ftpPassword || '',
      ftpRemotePath: restaurant.ftpRemotePath || '',
      ftpUseSsl: restaurant.ftpUseSsl || false,
      footerWarningSp: restaurant.footerWarningSp || '',
      footerWarningEn: restaurant.footerWarningEn || '',
    });
  }

  function resetRestaurantForm() {
    setEditingRestaurantId(null);
    setRestaurantForm(emptyRestaurant);
  }

  async function saveRestaurant(event) {
    event.preventDefault();
    await runMutation(
      () => fetch(editingRestaurantId ? `/api/restaurants/${editingRestaurantId}` : '/api/restaurants', {
        method: editingRestaurantId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: restaurantForm.name.trim(),
          address: restaurantForm.address.trim() || null,
          publicSlug: restaurantForm.publicSlug.trim() || null,
          isActive: restaurantForm.isActive,
          ftpEnabled: restaurantForm.ftpEnabled,
          ftpHost: restaurantForm.ftpHost.trim() || null,
          ftpPort: Number(restaurantForm.ftpPort || 21),
          ftpUserName: restaurantForm.ftpUserName.trim() || null,
          ftpPassword: restaurantForm.ftpPassword || null,
          ftpRemotePath: restaurantForm.ftpRemotePath.trim() || null,
          ftpUseSsl: restaurantForm.ftpUseSsl,
          footerWarningSp: restaurantForm.footerWarningSp.trim() || null,
          footerWarningEn: restaurantForm.footerWarningEn.trim() || null,
          businessId: activeBusinessId,
        }),
      }),
      editingRestaurantId ? 'Restaurante actualizado.' : 'Restaurante creado.',
    );
    resetRestaurantForm();
  }

  async function deleteRestaurant(restaurant) {
    if (!window.confirm(`Quieres eliminar el restaurante "${restaurant.name}"? Esta accion solo se permite si no tiene menus guardados.`)) {
      return;
    }

    await runMutation(
      () => fetch(`/api/restaurants/${restaurant.id}${activeBusinessId ? `?${new URLSearchParams({ businessId: activeBusinessId }).toString()}` : ''}`, { method: 'DELETE' }),
      'Restaurante eliminado.',
    );
    if (editingRestaurantId === restaurant.id) {
      resetRestaurantForm();
    }
  }

  function editUser(user) {
    setEditingUserId(user.id);
    setUserForm({
      userName: user.userName,
      displayName: user.displayName,
      password: '',
      role: user.role,
      isActive: user.isActive,
      restaurantIds: user.restaurantIds || [],
    });
  }

  function resetUserForm() {
    setEditingUserId(null);
    setUserForm(emptyUser);
  }

  function toggleUserRestaurant(restaurantId) {
    const selected = new Set(userForm.restaurantIds);
    if (selected.has(restaurantId)) {
      selected.delete(restaurantId);
    } else {
      selected.add(restaurantId);
    }
    setUserForm({ ...userForm, restaurantIds: Array.from(selected) });
  }

  async function saveUser(event) {
    event.preventDefault();
    if ((userForm.role === 'Manager' || userForm.role === 'Viewer') && userForm.restaurantIds.length === 0) {
      setError('Manager y Viewer deben tener al menos un restaurante asignado.');
      return;
    }

    await runMutation(
      () => fetch(editingUserId ? `/api/users/${editingUserId}` : '/api/users', {
        method: editingUserId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: userForm.userName.trim(),
          displayName: userForm.displayName.trim(),
          password: userForm.password || null,
          role: userForm.role,
          isActive: userForm.isActive,
          restaurantIds: userForm.restaurantIds,
          businessId: activeBusinessId,
        }),
      }),
      editingUserId ? 'Usuario actualizado.' : 'Usuario creado.',
    );
    resetUserForm();
    await loadUsers();
  }

  function editBusiness(business) {
    setEditingBusinessId(business.id);
    setBusinessForm({
      name: business.name,
      publicSlug: business.publicSlug || '',
      isActive: business.isActive,
    });
  }

  function resetBusinessForm() {
    setEditingBusinessId(null);
    setBusinessForm(emptyBusiness);
  }

  async function saveBusiness(event) {
    event.preventDefault();
    await runMutation(
      () => fetch(editingBusinessId ? `/api/businesses/${editingBusinessId}` : '/api/businesses', {
        method: editingBusinessId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: businessForm.name.trim(),
          publicSlug: businessForm.publicSlug.trim() || null,
          isActive: businessForm.isActive,
        }),
      }),
      editingBusinessId ? 'Negocio actualizado.' : 'Negocio creado.',
    );
    resetBusinessForm();
    await loadBusinessesForUser(currentUser);
  }

  async function resetUserPassword(user) {
    if (!window.confirm(`Quieres resetear la clave de "${user.displayName}" a Temp123!?`)) {
      return;
    }

    await runMutation(
      () => fetch(`/api/users/${user.id}/reset-password`, { method: 'POST' }),
      'Clave reseteada a Temp123!.',
    );
    await loadUsers();
  }

  async function saveProduct(event) {
    event.preventDefault();
    await runMutation(
      () => fetch(editingProductId ? `/api/products/${editingProductId}` : '/api/products', {
        method: editingProductId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productPayload(productForm, activeCategoryId, activeBusinessId)),
      }),
      editingProductId ? 'Producto actualizado.' : 'Producto creado.',
    );
    resetProductForm();
  }

  async function deleteProduct(product) {
    if (!window.confirm(`Quieres eliminar "${primaryProductName(product)}"?`)) {
      return;
    }

    await runMutation(
      () => fetch(`/api/products/${product.id}`, { method: 'DELETE' }),
      'Producto eliminado.',
    );
  }

  const activeCategory = useMemo(
    () => categories.find((category) => category.id === activeCategoryId),
    [activeCategoryId, categories],
  );

  const activeRestaurant = useMemo(
    () => restaurants.find((restaurant) => restaurant.id === activeRestaurantId),
    [activeRestaurantId, restaurants],
  );

  const activeMenu = useMemo(
    () => menu.find((category) => category.id === activeCategoryId),
    [activeCategoryId, menu],
  );

  const menuItems = useMemo(
    () => menu.reduce((total, category) => total + category.items.length, 0),
    [menu],
  );

  const selectedProductIds = useMemo(
    () => new Set(activeMenu?.items.map((item) => item.productID) ?? []),
    [activeMenu],
  );

  const filteredProducts = useMemo(() => {
    const needle = normalizeText(search);
    if (!needle) {
      return products;
    }

    return products.filter((product) =>
      normalizeText(product.nameEn).includes(needle) ||
      normalizeText(product.nameSp).includes(needle));
  }, [products, search]);

  const canAddMore = menuItems < 25;
  const specialLimitReached = activeCategory?.order === 1 && (activeMenu?.items.length ?? 0) >= 3;
  const hasSystemAccess = currentUser?.role === 'System';
  const hasAdminAccess = currentUser?.role === 'Admin' || currentUser?.role === 'System';
  const canEditMenu = (hasAdminAccess || currentUser?.role === 'Manager') && !menuStatus?.isClosed;
  const visibleMode = phoneMode ? 'menu' : mode;

  if (!authChecked) {
    return <main className="login-shell"><div className="status-panel">Validando sesion...</div></main>;
  }

  if (!currentUser) {
    return (
      <LoginScreen
        error={error}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        onLogin={login}
      />
    );
  }

  if (currentUser.mustChangePassword || showPasswordPanel) {
    return (
      <PasswordScreen
        currentUser={currentUser}
        error={error}
        notice={notice}
        passwordForm={passwordForm}
        requireChange={currentUser.mustChangePassword}
        setPasswordForm={setPasswordForm}
        onCancel={() => setShowPasswordPanel(false)}
        onChangePassword={changePassword}
        onLogout={logout}
      />
    );
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <BrandMark compact />
          <h1>Panel de menu</h1>
        </div>

        <div className="mode-tabs">
          <button className={visibleMode === 'menu' ? 'active' : ''} onClick={() => setMode('menu')} type="button">Menu</button>
          {!phoneMode && <button className={mode === 'history' ? 'active' : ''} onClick={() => setMode('history')} type="button">Historial</button>}
          {!phoneMode && hasSystemAccess && <button className={mode === 'businesses' ? 'active' : ''} onClick={() => setMode('businesses')} type="button">Negocios</button>}
          {!phoneMode && hasAdminAccess && <button className={mode === 'catalog' ? 'active' : ''} onClick={() => setMode('catalog')} type="button">Catalogo</button>}
          {!phoneMode && hasAdminAccess && <button className={mode === 'reports' ? 'active' : ''} onClick={() => setMode('reports')} type="button">Reportes</button>}
          {!phoneMode && hasSystemAccess && <button className={mode === 'logs' ? 'active' : ''} onClick={() => setMode('logs')} type="button">Logs</button>}
          {!phoneMode && hasAdminAccess && <button className={mode === 'config' ? 'active' : ''} onClick={() => setMode('config')} type="button">Config</button>}
        </div>

        <nav className="category-list" aria-label="Categorias">
          {categories.map((category, index) => (
            <div className={category.id === activeCategoryId ? 'category-row active' : 'category-row'} key={category.id}>
              <button
                className="category-button"
                onClick={() => {
                  setSearch('');
                  setActiveCategoryId(category.id);
                }}
                type="button"
              >
                <span>{categoryDisplayName(category)}</span>
                <strong>{category.menuProductCount}</strong>
              </button>
              {!phoneMode && mode === 'catalog' && (
                <div className="category-tools">
                  <button aria-label="Subir categoria" disabled={index === 0} onClick={() => moveCategory(category.id, -1)} title="Subir" type="button"><ArrowUpIcon /></button>
                  <button aria-label="Bajar categoria" disabled={index === categories.length - 1} onClick={() => moveCategory(category.id, 1)} title="Bajar" type="button"><ArrowDownIcon /></button>
                  <button aria-label="Editar categoria" onClick={() => editCategory(category)} title="Editar" type="button"><EditIcon /></button>
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <section className="workspace">
        <header className="toolbar">
          <div>
            <p className="eyebrow">{visibleMode === 'menu' ? 'Seleccion actual' : visibleMode === 'history' ? 'Archivo' : visibleMode === 'catalog' ? 'Administracion' : visibleMode === 'reports' ? 'Analisis' : visibleMode === 'logs' ? 'Soporte' : visibleMode === 'businesses' ? 'Sistema' : 'Configuracion'}</p>
            <h2>{visibleMode === 'config' ? (config.companyName || 'Menu Creator') : visibleMode === 'history' ? 'Historial de menus' : visibleMode === 'reports' ? 'Reportes' : visibleMode === 'logs' ? 'Logs del sistema' : visibleMode === 'businesses' ? 'Negocios' : categoryDisplayName(activeCategory)}</h2>
            {activeRestaurant && <p className="active-restaurant">{activeRestaurant.name}</p>}
            {currentUser.role === 'System' && (
              <label className="restaurant-picker">
                Negocio
                <select value={activeBusinessId ?? ''} onChange={(event) => changeBusiness(Number(event.target.value))}>
                  {businesses.map((business) => (
                    <option key={business.id} value={business.id}>{business.name}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="restaurant-picker">
              Restaurante
              <select value={activeRestaurantId ?? ''} onChange={(event) => changeRestaurant(Number(event.target.value))}>
                {restaurants.map((restaurant) => (
                  <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                ))}
              </select>
            </label>
            <label className="restaurant-picker">
              Fecha del menu
              <input type="date" value={configForm.dateShowed} onChange={(event) => setConfigForm({ ...configForm, dateShowed: event.target.value })} />
            </label>
          </div>
          <div className="toolbar-side">
            <div className="metrics">
              <span>
                <strong>{restaurants.length}</strong>
                restaurantes
              </span>
              <span>
                <strong>{categories.length}</strong>
                categorias
              </span>
              <span className={menuItems >= 25 ? 'limit' : ''}>
                <strong>{menuItems}</strong>
                de 25 items
              </span>
            </div>
            <div className={showUserMenu ? 'user-menu open' : 'user-menu'} ref={userMenuRef}>
              <button className="user-menu-button" onClick={() => setShowUserMenu((current) => !current)} type="button">
                <span>
                  <strong>{currentUser.displayName}</strong>
                  <small>{currentUser.role}</small>
                </span>
                <b>{showUserMenu ? '▲' : '▼'}</b>
              </button>
              <div className="user-menu-panel">
                <button className="text-button secondary" onClick={() => { setShowUserMenu(false); setShowPasswordPanel(true); }} tabIndex={showUserMenu ? 0 : -1} type="button">Cambiar clave</button>
                <button className="text-button secondary" onClick={logout} tabIndex={showUserMenu ? 0 : -1} type="button">Salir</button>
              </div>
            </div>
          </div>
        </header>

        {loading && <div className="status-panel">Cargando menu...</div>}
        {!loading && error && <div className="status-panel error">{error}</div>}
        {!loading && notice && !error && <div className="status-panel success">{notice}</div>}

        {!loading && !error && visibleMode === 'menu' && (
          <MenuWorkspace
            activeMenu={activeMenu}
            activeRestaurant={activeRestaurant}
            busyProductId={busyProductId}
            canAddMore={canAddMore}
            copyForm={copyForm}
            filteredProducts={filteredProducts}
            importForm={importForm}
            menuDate={configForm.dateShowed || config.dateShowed}
            menuHistory={menuHistory}
            menuStatus={menuStatus}
            productsLoading={productsLoading}
            restaurants={restaurants}
            search={search}
            selectedProductIds={selectedProductIds}
            setCopyForm={setCopyForm}
            setImportForm={setImportForm}
            setSearch={setSearch}
            showCopyPanel={showCopyPanel}
            specialLimitReached={specialLimitReached}
            canEditMenu={canEditMenu}
            onAdd={addProductToMenu}
            onClear={clearMenu}
            onCopyMenu={copyMenu}
            onExport={openExport}
            onImportMenu={importMenu}
            onMove={moveMenuProduct}
            onRemove={removeProductFromMenu}
            onUpdateMenuStatus={updateMenuStatus}
            onToggleCopyPanel={() => setShowCopyPanel((current) => !current)}
            onSendFtp={sendMenuToFtp}
          />
        )}

        {!loading && !error && !phoneMode && mode === 'catalog' && (
          <CatalogWorkspace
            activeCategory={activeCategory}
            catalogImportForm={catalogImportForm}
            categories={categories}
            categoryForm={categoryForm}
            editingCategoryId={editingCategoryId}
            editingProductId={editingProductId}
            filteredProducts={filteredProducts}
            productForm={productForm}
            productsLoading={productsLoading}
            search={search}
            setCategoryForm={setCategoryForm}
            setCatalogImportForm={setCatalogImportForm}
            setProductForm={setProductForm}
            setSearch={setSearch}
            onCancelCategory={resetCategoryForm}
            onCancelProduct={resetProductForm}
            onDeleteCategory={deleteCategory}
            onDeleteProduct={deleteProduct}
            onEditProduct={editProduct}
            onImportCatalog={importCatalog}
            onSaveCategory={saveCategory}
            onSaveProduct={saveProduct}
          />
        )}

        {!loading && !error && !phoneMode && mode === 'history' && (
          <HistoryWorkspace
            activeRestaurant={activeRestaurant}
            canEditMenu={canEditMenu}
            menuHistory={menuHistory}
            onCopy={prepareHistoryCopy}
            onOpen={openHistoryMenu}
          />
        )}

        {!loading && !error && !phoneMode && mode === 'reports' && (
          <ReportsWorkspace
            reports={reports}
            activeRestaurant={activeRestaurant}
            onRefresh={loadReports}
          />
        )}

        {!loading && !error && !phoneMode && mode === 'businesses' && (
          <BusinessWorkspace
            businessForm={businessForm}
            businesses={businesses}
            editingBusinessId={editingBusinessId}
            setBusinessForm={setBusinessForm}
            onCancelBusiness={resetBusinessForm}
            onEditBusiness={editBusiness}
            onSaveBusiness={saveBusiness}
          />
        )}

        {!loading && !error && !phoneMode && hasSystemAccess && mode === 'logs' && (
          <LogsWorkspace
            logs={logs}
            logDates={logDates}
            logFilter={logFilter}
            setLogFilter={setLogFilter}
            onLoadLogs={loadLogs}
          />
        )}

        {!loading && !error && !phoneMode && mode === 'config' && (
          <ConfigWorkspace
            configForm={configForm}
            currentUser={currentUser}
            editingRestaurantId={editingRestaurantId}
            editingUserId={editingUserId}
            restaurantForm={restaurantForm}
            restaurants={restaurants}
            userForm={userForm}
            users={users}
            setConfigForm={setConfigForm}
            setRestaurantForm={setRestaurantForm}
            setUserForm={setUserForm}
            onCancelRestaurant={resetRestaurantForm}
            onCancelUser={resetUserForm}
            onEditRestaurant={editRestaurant}
            onEditUser={editUser}
            onDeleteRestaurant={deleteRestaurant}
            onExport={openExport}
            onResetUserPassword={resetUserPassword}
            onSaveRestaurant={saveRestaurant}
            onSaveUser={saveUser}
            onSaveConfig={saveConfig}
            onToggleUserRestaurant={toggleUserRestaurant}
          />
        )}
      </section>
    </main>
  );
}

function LoginScreen({ error, loginForm, setLoginForm, onLogin }) {
  return (
    <main className="login-shell">
      <div className="login-art left-art" aria-hidden="true">
        <img className="castle-art" src={castleArt} alt="" />
      </div>
      <section className="login-panel">
        <BrandMark showCuisine={false} />
        {error && <div className="status-panel error">{error}</div>}
        <form className="data-form config-form" onSubmit={onLogin}>
          <label>
            Usuario
            <input placeholder="Usuario" required value={loginForm.userName} onChange={(event) => setLoginForm({ ...loginForm, userName: event.target.value })} />
          </label>
          <label>
            Contrasena
            <input placeholder="Contrasena" required type="password" value={loginForm.password} onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={loginForm.rememberMe} type="checkbox" onChange={(event) => setLoginForm({ ...loginForm, rememberMe: event.target.checked })} />
            Mantener sesion
          </label>
          <button className="text-button login-submit" type="submit">Iniciar sesion</button>
        </form>
      </section>
      <div className="login-food-panel" aria-hidden="true">
        <img className="olive-branch-art" src={oliveBranchArt} alt="" />
        <img className="mixed-art" src={mixedArt} alt="" />
      </div>
    </main>
  );
}

function PasswordScreen({
  currentUser,
  error,
  notice,
  passwordForm,
  requireChange,
  setPasswordForm,
  onCancel,
  onChangePassword,
  onLogout,
}) {
  return (
    <main className="login-shell">
      <div className="login-art left-art" aria-hidden="true">
        <img className="castle-art" src={castleArt} alt="" />
      </div>
      <section className="login-panel">
        <BrandMark />
        <p className="password-user">{currentUser.displayName}</p>
        {requireChange && <div className="status-panel warning">Debes cambiar la contrasena temporal antes de continuar.</div>}
        {error && <div className="status-panel error">{error}</div>}
        {notice && !error && <div className="status-panel success">{notice}</div>}
        <form className="data-form config-form" onSubmit={onChangePassword}>
          <label>
            Contrasena actual
            <input placeholder="Contrasena actual" required type="password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })} />
          </label>
          <label>
            Nueva contrasena
            <input placeholder="Nueva contrasena" required minLength="8" type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })} />
          </label>
          <label>
            Confirmar nueva contrasena
            <input placeholder="Confirmar nueva contrasena" required minLength="8" type="password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })} />
          </label>
          <div className="form-actions">
            <button className="text-button" type="submit">Guardar contrasena</button>
            {!requireChange && <button className="text-button secondary" onClick={onCancel} type="button">Cancelar</button>}
            <button className="text-button secondary" onClick={onLogout} type="button">Salir</button>
          </div>
        </form>
      </section>
      <div className="login-food-panel" aria-hidden="true">
        <img className="olive-branch-art" src={oliveBranchArt} alt="" />
        <img className="mixed-art" src={mixedArt} alt="" />
      </div>
    </main>
  );
}

function MenuWorkspace({
  activeMenu,
  activeRestaurant,
  busyProductId,
  canAddMore,
  canEditMenu,
  copyForm,
  filteredProducts,
  importForm,
  menuDate,
  menuHistory,
  menuStatus,
  productsLoading,
  restaurants,
  search,
  selectedProductIds,
  setCopyForm,
  setImportForm,
  setSearch,
  showCopyPanel,
  specialLimitReached,
  onAdd,
  onClear,
  onCopyMenu,
  onExport,
  onImportMenu,
  onMove,
  onRemove,
  onSendFtp,
  onUpdateMenuStatus,
  onToggleCopyPanel,
}) {
  const statusLabel = menuStatus?.effectiveStatus === 'Closed'
    ? 'Cerrado'
    : menuStatus?.status === 'Published'
      ? 'Publicado'
      : 'Draft';
  const publicMenuPath = activeRestaurant?.publicSlug ? `/public/menu/${activeRestaurant.publicSlug}` : '';
  const publicMenuUrl = publicMenuPath ? `${window.location.origin}${publicMenuPath}` : '';
  const publicMenuReady = menuStatus?.status === 'Published' && !menuStatus?.isClosed && !!activeMenu?.items.length && !!publicMenuPath;
  const [toolsOpen, setToolsOpen] = useState(false);

  return (
    <div className="work-grid">
      <section className="menu-section available-products">
        <div className="section-header">
          <div>
            <p className="eyebrow">Disponibles</p>
            <h3>Productos</h3>
          </div>
          <input aria-label="Buscar productos" className="search-box" onChange={(event) => setSearch(event.target.value)} placeholder="Buscar..." value={search} />
        </div>

        {productsLoading ? <div className="empty-state">Cargando productos...</div> : (
          <ProductList
            busyProductId={busyProductId}
            canAddMore={canAddMore}
            canEditMenu={canEditMenu}
            products={filteredProducts}
            selectedProductIds={selectedProductIds}
            specialLimitReached={specialLimitReached}
            onAdd={onAdd}
          />
        )}
      </section>

      <section className="menu-section selected-menu">
        <div className="section-header">
          <div>
            <p className="eyebrow">Seleccionados</p>
            <h3>Items en menu</h3>
            <span className={`menu-status status-${(menuStatus?.effectiveStatus || menuStatus?.status || 'Draft').toLowerCase()}`}>{statusLabel}</span>
            <span className={activeMenu?.items.length >= 25 ? 'menu-count limit' : 'menu-count'}>{activeMenu?.items.length ?? 0}/25</span>
          </div>
          <div className="section-actions">
            {canEditMenu && (
              <button
                className="text-button secondary"
                onClick={() => onUpdateMenuStatus(menuStatus?.status === 'Published' ? 'Draft' : 'Published')}
                type="button"
              >
                {menuStatus?.status === 'Published' ? 'Volver a Draft' : 'Publicar'}
              </button>
            )}
            <button className="text-button secondary" disabled={!canEditMenu || !activeMenu?.items.length} onClick={onClear} type="button">Limpiar</button>
          </div>
        </div>

        {!canAddMore && <div className="rule-note">El menu ya alcanzo el maximo de 25 items.</div>}
        {specialLimitReached && <div className="rule-note">Lunch Special ya tiene sus 3 items.</div>}
        {menuStatus?.isClosed && <div className="rule-note">Este menu es de un dia anterior y esta cerrado para edicion.</div>}

        {activeMenu?.items.length ? (
          <div className="item-list">
            {activeMenu.items.map((item, index) => (
              <article className="menu-item" key={item.productID}>
                <div className="menu-copy">
                  <span className="item-order">{item.order}</span>
                  <h4>{primaryProductName(item)}</h4>
                  <p>{secondaryProductName(item)}</p>
                </div>
                <div className="menu-actions">
                  <strong className="price-value">{formatCurrency(item.price)}</strong>
                  <button aria-label="Subir item" className="mini-button" disabled={!canEditMenu || index === 0 || busyProductId === item.productID} onClick={() => onMove(item.productID, -1)} title="Subir" type="button"><ArrowUpIcon /></button>
                  <button aria-label="Bajar item" className="mini-button" disabled={!canEditMenu || index === activeMenu.items.length - 1 || busyProductId === item.productID} onClick={() => onMove(item.productID, 1)} title="Bajar" type="button"><ArrowDownIcon /></button>
                  <button aria-label="Quitar item" className="mini-button danger" disabled={!canEditMenu || busyProductId === item.productID} onClick={() => onRemove(item.productID)} title="Quitar" type="button"><TrashIcon /></button>
                </div>
              </article>
            ))}
          </div>
        ) : <div className="empty-state">Esta categoria aun no tiene items seleccionados.</div>}

        <div className={toolsOpen ? 'menu-tools open' : 'menu-tools'}>
          <button className="menu-tools-toggle" onClick={() => setToolsOpen((current) => !current)} type="button">
            <span>Herramientas del menu</span>
            <strong>{toolsOpen ? 'Ocultar' : 'Mostrar'}</strong>
          </button>

          <div className="menu-tools-body" aria-hidden={!toolsOpen}>
            <div className="tool-group">
              <div>
                <p className="eyebrow">Exportar</p>
                <h4>Salida del menu</h4>
              </div>
              <div className="export-actions">
                <button className="text-button" disabled={!activeMenu?.items.length} onClick={() => onExport('both', true)} tabIndex={toolsOpen ? 0 : -1} type="button">Imprimir</button>
                <button className="text-button secondary" disabled={!activeMenu?.items.length} onClick={() => onExport('both')} tabIndex={toolsOpen ? 0 : -1} type="button">Vista bilingue</button>
                <button className="text-button secondary" disabled={!activeMenu?.items.length} onClick={() => onExport('spanish')} tabIndex={toolsOpen ? 0 : -1} type="button">Espanol</button>
                <button className="text-button secondary" disabled={!activeMenu?.items.length} onClick={() => onExport('english')} tabIndex={toolsOpen ? 0 : -1} type="button">English</button>
              </div>
            </div>

            <div className={publicMenuReady ? 'public-menu-panel ready' : 'public-menu-panel'}>
              <div>
                <p className="eyebrow">Portal publico</p>
                <h4>{publicMenuReady ? 'Link activo para el portal principal' : 'Link disponible al publicar'}</h4>
                <p>{publicMenuReady ? publicMenuUrl : 'Este link mostrara el menu bilingue del dia cuando el menu este Publicado.'}</p>
              </div>
              <div className="public-menu-actions">
                {publicMenuPath && <a className="text-button secondary" href={publicMenuPath} rel="noreferrer" tabIndex={toolsOpen ? 0 : -1} target="_blank">Abrir link</a>}
                {publicMenuUrl && <button className="text-button secondary" onClick={() => navigator.clipboard?.writeText(publicMenuUrl)} tabIndex={toolsOpen ? 0 : -1} type="button">Copiar link</button>}
                <button className="text-button secondary" disabled={!publicMenuReady || !activeRestaurant?.ftpEnabled} onClick={onSendFtp} tabIndex={toolsOpen ? 0 : -1} type="button">Enviar PDF por FTP</button>
              </div>
            </div>

            {canEditMenu && (
              <form className="import-panel" onSubmit={onImportMenu}>
                <div>
                  <p className="eyebrow">Importar Excel</p>
                  <h4>Cargar menu desde archivo</h4>
                </div>
                <div className="import-controls">
                  <input
                    accept=".xlsx,.csv"
                    aria-label="Archivo Excel del menu"
                    onChange={(event) => setImportForm({ ...importForm, file: event.target.files?.[0] ?? null })}
                    tabIndex={toolsOpen ? 0 : -1}
                    type="file"
                  />
                  <label className="check-row">
                    <input checked={importForm.replaceExisting} tabIndex={toolsOpen ? 0 : -1} type="checkbox" onChange={(event) => setImportForm({ ...importForm, replaceExisting: event.target.checked })} />
                    Reemplazar menu actual
                  </label>
                  <button className="text-button" disabled={!importForm.file} tabIndex={toolsOpen ? 0 : -1} type="submit">Importar</button>
                </div>
              </form>
            )}

            {canEditMenu && (
              <div className="tool-group">
                <div className="tool-group-header">
                  <div>
                    <p className="eyebrow">Copiar menu</p>
                    <h4>Traer items a {menuDate}</h4>
                  </div>
                  <button className="text-button secondary" onClick={onToggleCopyPanel} tabIndex={toolsOpen ? 0 : -1} type="button">
                    {showCopyPanel ? 'Ocultar copia' : 'Copiar menu'}
                  </button>
                </div>
                <div className={showCopyPanel ? 'copy-panel-shell open' : 'copy-panel-shell'}>
                  <div className="copy-panel" aria-hidden={!showCopyPanel}>
                    <div className="copy-controls">
                      <label>
                        Restaurante origen
                        <select tabIndex={toolsOpen && showCopyPanel ? 0 : -1} value={copyForm.sourceRestaurantId} onChange={(event) => setCopyForm({ ...copyForm, sourceRestaurantId: event.target.value })}>
                          <option value="">Selecciona</option>
                          {restaurants.map((restaurant) => (
                            <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Fecha origen
                        <input tabIndex={toolsOpen && showCopyPanel ? 0 : -1} type="date" value={copyForm.sourceDate} onChange={(event) => setCopyForm({ ...copyForm, sourceDate: event.target.value })} />
                      </label>
                      <label className="check-row">
                        <input checked={copyForm.replaceExisting} tabIndex={toolsOpen && showCopyPanel ? 0 : -1} type="checkbox" onChange={(event) => setCopyForm({ ...copyForm, replaceExisting: event.target.checked })} />
                        Reemplazar actual
                      </label>
                      <button className="text-button" onClick={onCopyMenu} tabIndex={toolsOpen && showCopyPanel ? 0 : -1} type="button">Copiar</button>
                    </div>
                    {!!menuHistory.length && (
                      <div className="history-list">
                        {menuHistory.slice(0, 6).map((entry) => (
                          <button
                            className="history-chip"
                            key={entry.id}
                            onClick={() => setCopyForm({
                              ...copyForm,
                              sourceRestaurantId: entry.restaurantId,
                              sourceDate: (entry.menuDate || '').slice(0, 10),
                            })}
                            tabIndex={toolsOpen && showCopyPanel ? 0 : -1}
                            type="button"
                          >
                            <strong>{(entry.menuDate || '').slice(0, 10)}</strong>
                            <span>{entry.itemCount} items</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function HistoryWorkspace({ activeRestaurant, canEditMenu, menuHistory, onCopy, onOpen }) {
  function effectiveStatus(entry) {
    const date = (entry.menuDate || '').slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    if (date < today) {
      return 'Cerrado';
    }
    return entry.status === 'Published' ? 'Publicado' : 'Draft';
  }

  return (
    <section className="menu-section history-workspace">
      <div className="section-header">
        <div>
          <p className="eyebrow">{activeRestaurant?.name ?? 'Restaurante'}</p>
          <h3>Menus recientes</h3>
        </div>
      </div>

      {menuHistory.length ? (
        <div className="history-table">
          {menuHistory.map((entry) => {
            const status = effectiveStatus(entry);
            const statusClass = status === 'Cerrado' ? 'closed' : status === 'Publicado' ? 'published' : 'draft';

            return (
              <article className="history-row" key={entry.id}>
                <div>
                  <span className="item-order">{(entry.menuDate || '').slice(0, 10)}</span>
                  <h4>{entry.restaurantName || activeRestaurant?.name || 'Restaurante'}</h4>
                  <p>Actualizado {(entry.updatedAt || '').slice(0, 10)} - {entry.itemCount} items</p>
                </div>
                <div className="history-row-actions">
                  <span className={`menu-status status-${statusClass.toLowerCase()}`}>{status}</span>
                  <button className="text-button secondary" onClick={() => onOpen(entry)} type="button">Abrir</button>
                  {canEditMenu && <button className="text-button" onClick={() => onCopy(entry)} type="button">Usar para copiar</button>}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">Todavia no hay menus guardados para este restaurante.</div>
      )}
    </section>
  );
}

function ReportsWorkspace({ reports, activeRestaurant, onRefresh }) {
  return (
    <div className="reports-workspace">
      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">{activeRestaurant?.name ?? 'Todos los restaurantes'}</p>
            <h3>Resumen operativo</h3>
          </div>
          <button className="text-button secondary" onClick={onRefresh} type="button">Actualizar</button>
        </div>
        <div className="metrics report-metrics">
          <span><strong>{reports.menuCount}</strong> menus</span>
          <span><strong>{reports.publishedCount}</strong> publicados</span>
          <span><strong>{reports.draftCount}</strong> draft</span>
          <span><strong>{reports.closedCount}</strong> cerrados</span>
          <span><strong>{reports.activeProducts}</strong> productos activos</span>
          <span className={reports.inactiveProducts ? 'limit' : ''}><strong>{reports.inactiveProducts}</strong> inactivos</span>
        </div>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Uso</p>
            <h3>Productos mas usados</h3>
          </div>
        </div>
        <div className="history-table">
          {reports.topProducts.map((product) => (
            <article className="history-row" key={product.productId}>
              <div>
                <h4>{primaryProductName(product)}</h4>
                <p>{secondaryProductName(product)}</p>
              </div>
              <span className="status-badge active">{product.usageCount} usos</span>
            </article>
          ))}
          {!reports.topProducts.length && <div className="empty-state">Todavia no hay uso suficiente para reportar.</div>}
        </div>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Archivo</p>
            <h3>Menus recientes</h3>
          </div>
        </div>
        <div className="history-table">
          {reports.recentMenus.map((menu) => (
            <article className="history-row" key={menu.menuId}>
              <div>
                <span className="item-order">{(menu.menuDate || '').slice(0, 10)}</span>
                <h4>{menu.restaurantName}</h4>
                <p>{menu.itemCount} items</p>
              </div>
              <span className={`menu-status status-${menu.status.toLowerCase()}`}>{menu.status}</span>
            </article>
          ))}
          {!reports.recentMenus.length && <div className="empty-state">Todavia no hay menus registrados.</div>}
        </div>
      </section>
    </div>
  );
}

function LogsWorkspace({ logs, logDates, logFilter, setLogFilter, onLoadLogs }) {
  function updateFilter(next) {
    const filter = { ...logFilter, ...next };
    setLogFilter(filter);
    onLoadLogs(filter);
  }

  return (
    <section className="menu-section logs-workspace">
      <div className="section-header">
        <div>
          <p className="eyebrow">Soporte</p>
          <h3>Visor de logs</h3>
        </div>
        <button className="text-button secondary" onClick={() => onLoadLogs(logFilter)} type="button">Actualizar</button>
      </div>
      <div className="log-controls">
        <label>
          Fecha
          <select value={logFilter.date} onChange={(event) => updateFilter({ date: event.target.value })}>
            {logDates.map((date) => <option key={date} value={date}>{date}</option>)}
          </select>
        </label>
        <label>
          Tipo
          <select value={logFilter.type} onChange={(event) => updateFilter({ type: event.target.value })}>
            <option value="">Todos</option>
            <option value="Info">Info</option>
            <option value="Warning">Warning</option>
            <option value="Error">Error</option>
            <option value="Exception">Exception</option>
            <option value="Debug">Debug</option>
          </select>
        </label>
        <label>
          Buscar
          <input value={logFilter.search} onChange={(event) => setLogFilter({ ...logFilter, search: event.target.value })} onKeyDown={(event) => event.key === 'Enter' && onLoadLogs(logFilter)} />
        </label>
        <button className="text-button" onClick={() => onLoadLogs(logFilter)} type="button">Filtrar</button>
      </div>
      <div className="log-list">
        {logs.map((entry, index) => (
          <article className="log-row" key={`${entry.dateTime}-${index}`}>
            <span className={`status-badge log-${entry.logType.toLowerCase()}`}>{entry.logType}</span>
            <div>
              <strong>{entry.dateTime} | {entry.className}.{entry.methodName}</strong>
              <p>{entry.message}</p>
              {entry.values && <small>{entry.values}</small>}
            </div>
          </article>
        ))}
        {!logs.length && <div className="empty-state">No hay logs para ese filtro.</div>}
      </div>
    </section>
  );
}

function BusinessWorkspace({ businessForm, businesses, editingBusinessId, setBusinessForm, onCancelBusiness, onEditBusiness, onSaveBusiness }) {
  return (
    <div className="config-grid">
      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">System</p>
            <h3>{editingBusinessId ? 'Editar negocio' : 'Nuevo negocio'}</h3>
          </div>
        </div>
        <form className="data-form config-form" onSubmit={onSaveBusiness}>
          <label>
            Nombre
            <input required value={businessForm.name} onChange={(event) => setBusinessForm({ ...businessForm, name: event.target.value })} />
          </label>
          <label>
            Link interno
            <input placeholder="delicias-de-espana" value={businessForm.publicSlug} onChange={(event) => setBusinessForm({ ...businessForm, publicSlug: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={businessForm.isActive} type="checkbox" onChange={(event) => setBusinessForm({ ...businessForm, isActive: event.target.checked })} />
            Activo
          </label>
          <div className="form-actions">
            <button className="text-button" type="submit">{editingBusinessId ? 'Guardar' : 'Crear'}</button>
            {editingBusinessId && <button className="text-button secondary" onClick={onCancelBusiness} type="button">Cancelar</button>}
          </div>
        </form>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Negocios</p>
            <h3>Clientes principales</h3>
          </div>
        </div>
        <div className="restaurant-admin-list">
          {businesses.map((business) => (
            <article className="admin-row" key={business.id}>
              <div>
                <h4>{business.name}</h4>
                <p>{business.publicSlug}</p>
                <div className="chip-row">
                  <span className={business.isActive ? 'status-badge active' : 'status-badge inactive'}>{business.isActive ? 'Activo' : 'Inactivo'}</span>
                  <span className="data-chip">{business.restaurantCount} restaurantes</span>
                  <span className="data-chip">{business.userCount} usuarios</span>
                </div>
              </div>
              <div className="row-actions">
                <button aria-label="Editar negocio" className="mini-button" onClick={() => onEditBusiness(business)} title="Editar negocio" type="button"><EditIcon /></button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function CatalogWorkspace({
  activeCategory,
  catalogImportForm,
  categories,
  categoryForm,
  editingCategoryId,
  editingProductId,
  filteredProducts,
  productForm,
  productsLoading,
  search,
  setCategoryForm,
  setCatalogImportForm,
  setProductForm,
  setSearch,
  onCancelCategory,
  onCancelProduct,
  onDeleteCategory,
  onDeleteProduct,
  onEditProduct,
  onImportCatalog,
  onSaveCategory,
  onSaveProduct,
}) {
  return (
    <div className="catalog-grid">
      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Categorias</p>
            <h3>{editingCategoryId ? 'Editar categoria' : 'Nueva categoria'}</h3>
          </div>
        </div>

        <form className="data-form" onSubmit={onSaveCategory}>
          <label>
            Nombre interno
            <input required value={categoryForm.name} onChange={(event) => setCategoryForm({ ...categoryForm, name: event.target.value })} />
          </label>
          <label>
            Nombre impreso
            <input required value={categoryForm.printName} onChange={(event) => setCategoryForm({ ...categoryForm, printName: event.target.value })} />
          </label>
          <label>
            Espanol
            <input required value={categoryForm.nameSp} onChange={(event) => setCategoryForm({ ...categoryForm, nameSp: event.target.value })} />
          </label>
          <label>
            Ingles
            <input required value={categoryForm.nameEn} onChange={(event) => setCategoryForm({ ...categoryForm, nameEn: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={categoryForm.extern} type="checkbox" onChange={(event) => setCategoryForm({ ...categoryForm, extern: event.target.checked })} />
            Categoria externa
          </label>
          <div className="form-actions">
            <button className="text-button" type="submit">{editingCategoryId ? 'Guardar' : 'Crear'}</button>
            {editingCategoryId && <button className="text-button secondary" onClick={onCancelCategory} type="button">Cancelar</button>}
          </div>
        </form>

        <div className="category-admin-list">
          {categories.map((category) => (
            <article className="admin-row" key={category.id}>
              <div>
                <h4>{categoryDisplayName(category)}</h4>
                <p>{category.productCount} productos</p>
              </div>
              <div className="row-actions">
                <button aria-label="Eliminar categoria" className="mini-button danger" onClick={() => onDeleteCategory(category)} title="Eliminar categoria" type="button"><TrashIcon /></button>
              </div>
            </article>
          ))}
        </div>

        <form className="import-panel catalog-import-panel" onSubmit={onImportCatalog}>
          <div>
            <p className="eyebrow">Importar catalogo</p>
            <h4>Categorias y productos desde Excel</h4>
          </div>
          <div className="import-controls catalog-import-controls">
            <input
              accept=".xlsx,.csv"
              aria-label="Archivo Excel del catalogo"
              onChange={(event) => setCatalogImportForm({ ...catalogImportForm, file: event.target.files?.[0] ?? null })}
              type="file"
            />
            <label className="check-row">
              <input checked={catalogImportForm.updateExisting} type="checkbox" onChange={(event) => setCatalogImportForm({ ...catalogImportForm, updateExisting: event.target.checked })} />
              Actualizar existentes
            </label>
            <button className="text-button" disabled={!catalogImportForm.file} type="submit">Importar</button>
          </div>
        </form>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Productos</p>
            <h3>{categoryDisplayName(activeCategory)}</h3>
          </div>
          <input aria-label="Buscar productos" className="search-box" onChange={(event) => setSearch(event.target.value)} placeholder="Buscar..." value={search} />
        </div>

        <form className={`data-form product-form${editingProductId ? ' editing' : ''}`} onSubmit={onSaveProduct}>
          <label>
            Espanol
            <input value={productForm.nameSp} onChange={(event) => setProductForm({ ...productForm, nameSp: event.target.value })} />
          </label>
          <label>
            Ingles
            <input required value={productForm.nameEn} onChange={(event) => setProductForm({ ...productForm, nameEn: event.target.value })} />
          </label>
          <label>
            Precio
            <input
              className="price-input"
              inputMode="decimal"
              value={productForm.price}
              onBlur={(event) => setProductForm({ ...productForm, price: formatPriceInput(event.target.value) })}
              onChange={(event) => setProductForm({ ...productForm, price: event.target.value })}
              onFocus={(event) => setProductForm({ ...productForm, price: cleanPriceInput(event.target.value) })}
            />
          </label>
          <label className="check-row">
            <input checked={productForm.isActive} type="checkbox" onChange={(event) => setProductForm({ ...productForm, isActive: event.target.checked })} />
            Activo
          </label>
          <div className="form-actions">
            <button className="text-button" disabled={!activeCategory} type="submit">{editingProductId ? 'Guardar' : 'Crear'}</button>
            {editingProductId && <button className="text-button secondary" onClick={onCancelProduct} type="button">Cancelar</button>}
          </div>
        </form>

        {productsLoading ? <div className="empty-state">Cargando productos...</div> : (
          <div className="product-list">
            {filteredProducts.map((product) => (
              <article className="product-row" key={product.id}>
                <div>
                  <h4>{primaryProductName(product)}</h4>
                  <p>{secondaryProductName(product)}</p>
                </div>
                <div className="row-actions">
                  {!product.isActive && <span className="status-badge inactive">Inactivo</span>}
                  <strong className="price-value">{formatCurrency(product.price)}</strong>
                  <button aria-label="Editar producto" className="mini-button" onClick={() => onEditProduct(product)} title="Editar producto" type="button"><EditIcon /></button>
                  <button aria-label="Eliminar producto" className="mini-button danger" onClick={() => onDeleteProduct(product)} title="Eliminar producto" type="button"><TrashIcon /></button>
                </div>
              </article>
            ))}
            {!filteredProducts.length && <div className="empty-state">No hay productos con ese filtro.</div>}
          </div>
        )}
      </section>
    </div>
  );
}

function ConfigWorkspace({
  configForm,
  currentUser,
  editingRestaurantId,
  editingUserId,
  restaurantForm,
  restaurants,
  userForm,
  users,
  setConfigForm,
  setRestaurantForm,
  setUserForm,
  onCancelRestaurant,
  onCancelUser,
  onDeleteRestaurant,
  onEditRestaurant,
  onEditUser,
  onExport,
  onResetUserPassword,
  onSaveRestaurant,
  onSaveUser,
  onSaveConfig,
  onToggleUserRestaurant,
}) {
  function restaurantName(restaurantId) {
    return restaurants.find((restaurant) => restaurant.id === restaurantId)?.name ?? `Restaurante ${restaurantId}`;
  }

  return (
    <div className="config-grid">
      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Datos del menu</p>
            <h3>Configuracion</h3>
          </div>
        </div>

        <form className="data-form config-form" onSubmit={onSaveConfig}>
          <label>
            Restaurante / compania
            <input required value={configForm.companyName} onChange={(event) => setConfigForm({ ...configForm, companyName: event.target.value })} />
          </label>
          <label>
            Fecha del menu
            <input required type="date" value={configForm.dateShowed} onChange={(event) => setConfigForm({ ...configForm, dateShowed: event.target.value })} />
          </label>
          <label>
            Ruta legado
            <input value={configForm.pathFile} onChange={(event) => setConfigForm({ ...configForm, pathFile: event.target.value })} />
          </label>
          <label>
            Advertencia en espanol
            <textarea rows="5" value={configForm.footerWarningSp} onChange={(event) => setConfigForm({ ...configForm, footerWarningSp: event.target.value })} />
          </label>
          <label>
            Warning in English
            <textarea rows="5" value={configForm.footerWarningEn} onChange={(event) => setConfigForm({ ...configForm, footerWarningEn: event.target.value })} />
          </label>
          <div className="form-actions">
            <button className="text-button" type="submit">Guardar configuracion</button>
          </div>
        </form>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Acceso</p>
            <h3>{editingUserId ? 'Editar usuario' : 'Nuevo usuario'}</h3>
          </div>
        </div>

        <form className="data-form config-form" onSubmit={onSaveUser}>
          <label>
            Usuario
            <input required value={userForm.userName} onChange={(event) => setUserForm({ ...userForm, userName: event.target.value })} />
          </label>
          <label>
            Nombre
            <input required value={userForm.displayName} onChange={(event) => setUserForm({ ...userForm, displayName: event.target.value })} />
          </label>
          <label>
            Contrasena
            <input placeholder={editingUserId ? 'Dejar vacia para mantener' : 'Temp123! si se deja vacia'} type="password" value={userForm.password} onChange={(event) => setUserForm({ ...userForm, password: event.target.value })} />
          </label>
          <label>
            Rol
            <select value={userForm.role} onChange={(event) => setUserForm({ ...userForm, role: event.target.value })}>
              {currentUser.role === 'System' && <option value="System">System</option>}
              <option value="Admin">Admin</option>
              <option value="Manager">Manager</option>
              <option value="Viewer">Viewer</option>
            </select>
          </label>
          <label className="check-row">
            <input
              checked={userForm.isActive}
              disabled={editingUserId === currentUser.id}
              type="checkbox"
              onChange={(event) => setUserForm({ ...userForm, isActive: event.target.checked })}
            />
            Activo
          </label>
          {editingUserId === currentUser.id && <p className="form-note">Tu propio usuario debe permanecer activo con acceso administrativo.</p>}
          <div className="restaurant-checks">
            {restaurants.map((restaurant) => (
              <label className="check-row" key={restaurant.id}>
                <input checked={userForm.restaurantIds.includes(restaurant.id)} type="checkbox" onChange={() => onToggleUserRestaurant(restaurant.id)} />
                {restaurant.name}
              </label>
            ))}
          </div>
          <div className="form-actions">
            <button className="text-button" type="submit">{editingUserId ? 'Guardar' : 'Crear'}</button>
            {editingUserId && <button className="text-button secondary" onClick={onCancelUser} type="button">Cancelar</button>}
          </div>
        </form>

        <div className="restaurant-admin-list">
          {users.map((user) => (
            <article className="admin-row" key={user.id}>
              <div>
                <h4>{user.displayName}</h4>
                <p>{user.userName}</p>
                <div className="badge-row">
                  <span className={`status-badge role-${user.role.toLowerCase()}`}>{user.role}</span>
                  <span className={user.isActive ? 'status-badge active' : 'status-badge inactive'}>{user.isActive ? 'Activo' : 'Inactivo'}</span>
                  {user.mustChangePassword && <span className="status-badge warning">Clave temporal</span>}
                </div>
                <div className="chip-row">
                  {(user.restaurantIds || []).map((restaurantId) => (
                    <span className="data-chip" key={restaurantId}>{restaurantName(restaurantId)}</span>
                  ))}
                  {!user.restaurantIds?.length && <span className="data-chip muted">Sin restaurantes</span>}
                </div>
              </div>
              <div className="row-actions">
                <button aria-label="Editar usuario" className="mini-button" onClick={() => onEditUser(user)} title="Editar usuario" type="button"><EditIcon /></button>
                <button aria-label="Resetear clave" className="mini-button" onClick={() => onResetUserPassword(user)} title="Resetear clave" type="button"><KeyIcon /></button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Restaurantes</p>
            <h3>{editingRestaurantId ? 'Editar restaurante' : 'Nuevo restaurante'}</h3>
          </div>
        </div>

        <form className="data-form config-form" onSubmit={onSaveRestaurant}>
          <label>
            Nombre
            <input required value={restaurantForm.name} onChange={(event) => setRestaurantForm({ ...restaurantForm, name: event.target.value })} />
          </label>
          <label>
            Direccion
            <input value={restaurantForm.address} onChange={(event) => setRestaurantForm({ ...restaurantForm, address: event.target.value })} />
          </label>
          <label>
            Link publico
            <input placeholder="delicias-espana-ii" value={restaurantForm.publicSlug} onChange={(event) => setRestaurantForm({ ...restaurantForm, publicSlug: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={restaurantForm.ftpEnabled} type="checkbox" onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpEnabled: event.target.checked })} />
            Envio FTP activo
          </label>
          <label>
            FTP host
            <input placeholder="ftp.midominio.com" value={restaurantForm.ftpHost} onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpHost: event.target.value })} />
          </label>
          <label>
            FTP puerto
            <input min="1" type="number" value={restaurantForm.ftpPort} onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpPort: event.target.value })} />
          </label>
          <label>
            FTP usuario
            <input value={restaurantForm.ftpUserName} onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpUserName: event.target.value })} />
          </label>
          <label>
            FTP contrasena
            <input type="password" value={restaurantForm.ftpPassword} onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpPassword: event.target.value })} />
          </label>
          <label>
            Ruta remota PDF
            <input placeholder="/public_html/menu/" value={restaurantForm.ftpRemotePath} onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpRemotePath: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={restaurantForm.ftpUseSsl} type="checkbox" onChange={(event) => setRestaurantForm({ ...restaurantForm, ftpUseSsl: event.target.checked })} />
            Usar FTPS / SSL
          </label>
          <label>
            Advertencia en espanol
            <textarea rows="4" placeholder="Vacia para usar la advertencia global" value={restaurantForm.footerWarningSp} onChange={(event) => setRestaurantForm({ ...restaurantForm, footerWarningSp: event.target.value })} />
          </label>
          <label>
            Warning in English
            <textarea rows="4" placeholder="Empty to use global warning" value={restaurantForm.footerWarningEn} onChange={(event) => setRestaurantForm({ ...restaurantForm, footerWarningEn: event.target.value })} />
          </label>
          <label className="check-row">
            <input checked={restaurantForm.isActive} type="checkbox" onChange={(event) => setRestaurantForm({ ...restaurantForm, isActive: event.target.checked })} />
            Activo
          </label>
          <div className="form-actions">
            <button className="text-button" type="submit">{editingRestaurantId ? 'Guardar' : 'Crear'}</button>
            {editingRestaurantId && <button className="text-button secondary" onClick={onCancelRestaurant} type="button">Cancelar</button>}
          </div>
        </form>

        <div className="restaurant-admin-list">
          {restaurants.map((restaurant) => (
            <article className="admin-row" key={restaurant.id}>
              <div>
                <h4>{restaurant.name}</h4>
                <p>{restaurant.address || 'Sin direccion'}{restaurant.isActive ? '' : ' - Inactivo'}</p>
                <div className="chip-row">
                  <span className="data-chip">/public/menu/{restaurant.publicSlug}</span>
                  {restaurant.ftpEnabled && <span className="data-chip">FTP activo</span>}
                  {restaurant.ftpLastStatus && <span className={restaurant.ftpLastStatus === 'Success' ? 'data-chip' : 'data-chip muted'}>FTP {restaurant.ftpLastStatus}</span>}
                </div>
                {restaurant.ftpLastSentAt && <p>Ultimo FTP {(restaurant.ftpLastSentAt || '').slice(0, 16)} {restaurant.ftpLastFileName || ''}</p>}
              </div>
              <div className="row-actions">
                <button aria-label="Editar restaurante" className="mini-button" onClick={() => onEditRestaurant(restaurant)} title="Editar restaurante" type="button"><EditIcon /></button>
                <button aria-label="Eliminar restaurante" className="mini-button danger" onClick={() => onDeleteRestaurant(restaurant)} title="Eliminar restaurante" type="button"><TrashIcon /></button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="menu-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">Vista imprimible</p>
            <h3>Exportar</h3>
          </div>
        </div>
        <div className="export-panel">
          <p>Usa la fecha y el nombre configurados para generar una version lista para imprimir.</p>
          <div className="export-actions">
            <button className="text-button" onClick={() => onExport('both', true)} type="button">Imprimir</button>
            <button className="text-button secondary" onClick={() => onExport('both')} type="button">Bilingue</button>
            <button className="text-button secondary" onClick={() => onExport('spanish')} type="button">Espanol</button>
            <button className="text-button secondary" onClick={() => onExport('english')} type="button">English</button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductList({ busyProductId, canAddMore, canEditMenu, products, selectedProductIds, specialLimitReached, onAdd }) {
  return (
    <div className="product-list">
      {products.map((product) => {
        const inMenu = selectedProductIds.has(product.id);
        const disabled = !canEditMenu || inMenu || !canAddMore || specialLimitReached || busyProductId === product.id;

        return (
          <article className={inMenu ? 'product-row selected' : 'product-row'} key={product.id}>
            <div>
              <h4>{primaryProductName(product)}</h4>
              <p>{secondaryProductName(product)}</p>
            </div>
            <div className="row-actions">
              <strong className="price-value">{formatCurrency(product.price)}</strong>
              <button aria-label="Agregar al menu" className="icon-button" disabled={disabled} onClick={() => onAdd(product)} title={inMenu ? 'Ya esta en menu' : 'Agregar al menu'} type="button"><PlusIcon /></button>
            </div>
          </article>
        );
      })}
      {!products.length && <div className="empty-state">No hay productos con ese filtro.</div>}
    </div>
  );
}
